import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users2, MessageSquare, Video, Layout, FileText as FileTextIcon, FolderOpen,
  Plus, Trash2, X, Send, FolderPlus, Upload, ExternalLink, Clock as ClockIcon, RotateCcw,
  Link2, Paperclip, Calendar, ChevronUp, ChevronDown, Maximize2, Minimize2,
} from "lucide-react";
import MDEditor from "@uiw/react-md-editor";
import { requestExtraScopes } from "@/lib/googleAuth";
import { scrollWithinContainer } from "@/lib/scroll";

// IMRaD-style scaffold pre-populated into every new paper draft so authors
// start with the standard sections + one placeholder sentence each.
const PAPER_TEMPLATE = `# Title of the Paper

## Abstract
*   Summary text goes here.

## 1. Introduction

### 1.1 Background and Context
Provide the background and set the context for this study.

### 1.2 Problem Statement
State the specific problem this paper addresses.

### 1.3 Research Objectives or Hypotheses
List the research objectives or the hypotheses under investigation.

### 1.4 Significance and Scope
Explain why this work matters and define its scope.

## 2. Theoretical Background and Related Work

### 2.1 Foundational Concepts
Introduce the foundational concepts relevant to this study.

### 2.2 Review of Previous Studies
Summarise the most relevant previous studies in this area.

### 2.3 Identification of the Knowledge Gap
Point out the specific gap this paper aims to fill.

## 3. Methodology and Approach

### 3.1 Research Design or System Architecture
Describe the research design or the overall system architecture.

### 3.2 Subjects, Data Sources, or Materials
Identify the subjects, data sources, or materials used.

### 3.3 Data Collection or Implementation Process
Explain how the data was collected or the implementation was carried out.

### 3.4 Analysis and Evaluation Framework
Describe the analysis techniques and evaluation framework applied.

## 4. Findings and Evaluation

### 4.1 Primary Results or Performance Metrics
Report the primary results or the performance metrics obtained.

### 4.2 Comparative Analysis or Detailed Observations
Present a comparative analysis or detailed observations from the study.

### 4.3 Statistical, Structural, or Thematic Outputs
Summarise the statistical, structural, or thematic outputs produced.

## 5. Discussion

### 5.1 Interpretation of Outcomes
Interpret what the outcomes mean in the broader context.

### 5.2 Theoretical and Practical Implications
Discuss the theoretical and practical implications of the findings.

### 5.3 Limitations and Trade-offs
Acknowledge the limitations and trade-offs of the current work.

## 6. Conclusion

### 6.1 Summary of Core Takeaways
Summarise the core takeaways from this paper.

### 6.2 Recommendations for Future Work
Recommend concrete directions for future research or development.

## References
*   Source list goes here.

## Appendices
*   Supplementary materials go here.
`;

const TABS = [
  { key: "team", label: "Team", icon: Users2 },
  { key: "tasks", label: "Tasks", icon: Layout },
  { key: "paper", label: "Paper Draft", icon: FileTextIcon },
  { key: "drive", label: "Drive Files", icon: FolderOpen },
];

const KANBAN_COLS = [
  { key: "backlog", label: "BACKLOG", cls: "border-slate-300 bg-slate-50" },
  { key: "in_progress", label: "IN PROGRESS", cls: "border-sky-300 bg-sky-50" },
  { key: "review", label: "REVIEW", cls: "border-amber-300 bg-amber-50" },
  { key: "done", label: "DONE", cls: "border-emerald-300 bg-emerald-50" },
];

const SCOPE_DRIVE = "openid email profile https://www.googleapis.com/auth/drive.file";
const SCOPE_MEET = "openid email profile https://www.googleapis.com/auth/meetings.space.created";

// ---------- DM Drawer (persistent floating chat) ----------
function DMDrawer({ partner, currentUserId, workspaceId, onClose, hasDrive }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]); // {name,url,size}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/dm/with/${partner.user_id}`);
      setMessages(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [partner.user_id]);
  useEffect(() => {
    scrollWithinContainer(endRef.current);
  }, [messages.length]);

  const onAttach = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!hasDrive) {
      toast.error("Connect Google Drive first to attach files");
      e.target.value = "";
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Max attachment size 25MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/google/drive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, { name: r.data.name, url: r.data.webViewLink, size: r.data.size, mimeType: r.data.mimeType }]);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const send = async () => {
    if (!draft.trim() && attachments.length === 0) return;
    try {
      const r = await api.post("/workspace/dm/send", {
        to_user_id: partner.user_id,
        body: draft.trim(),
        guidance_id: workspaceId,
        attachments,
      });
      setMessages((m) => [...m, r.data]);
      setDraft("");
      setAttachments([]);
    } catch (e) {
      toast.error("Failed to send");
    }
  };

  return (
    <div className="fixed top-0 right-0 z-40 h-screen w-[min(380px,calc(100vw-32px))] bg-white border-l border-slate-200 shadow-2xl flex flex-col" data-testid="dm-drawer">
      <div className="flex items-center gap-3 p-3 border-b border-slate-200 bg-kb-ink text-white">
        {partner.picture ? (
          <img src={partner.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-white/20" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center kb-mono text-xs">
            {partner.name?.[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{partner.name}</div>
          <div className="kb-mono text-[9px] uppercase tracking-wider text-white/60">{partner.role_in_workspace}</div>
        </div>
        <button onClick={onClose} className="p-1.5 text-white/70 hover:text-white" data-testid="dm-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {loading && <div className="text-xs text-kb-ink-muted text-center py-4">Loading…</div>}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-kb-ink-muted text-center py-4">No messages yet — say hi 👋</div>
        )}
        {messages.map((m) => {
          const mine = m.from_user_id === currentUserId;
          const atts = m.attachments || [];
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-sm text-sm ${mine ? "bg-kb-primary text-white" : "bg-white border border-slate-200 text-kb-ink"}`}>
                {m.body && <div>{m.body}</div>}
                {atts.length > 0 && (
                  <div className={`mt-1.5 space-y-1 ${m.body ? "pt-1.5 border-t " + (mine ? "border-white/20" : "border-slate-200") : ""}`}>
                    {atts.map((a, idx) => (
                      <a key={idx} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs underline opacity-90 hover:opacity-100">
                        <Paperclip className="w-3 h-3" /> {a.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {attachments.length > 0 && (
        <div className="px-3 py-2 border-t border-slate-200 bg-white space-y-1">
          {attachments.map((a, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs text-kb-ink-muted">
              <Paperclip className="w-3 h-3" /> {a.name}
              <button onClick={() => setAttachments((arr) => arr.filter((_, i) => i !== idx))} className="ml-auto text-rose-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="p-3 border-t border-slate-200 flex gap-2">
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="p-2 text-kb-ink-muted hover:text-kb-primary"
          data-testid="dm-attach-btn"
          title={hasDrive ? "Attach file (uploads to your Drive)" : "Connect Drive to attach files"}
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={onAttach} />
        <input
          className="kb-input text-sm flex-1"
          placeholder={uploading ? "Uploading…" : "Type a message…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="dm-input"
        />
        <button onClick={send} className="kb-btn-primary text-sm" data-testid="dm-send-btn">
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------- Team ----------
function TeamPanel({ participants, currentUserId, onMessage, onVideo }) {
  if (participants.length === 0) {
    return <div className="text-sm text-kb-ink-muted text-center py-8">No participants yet.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {participants.map((p) => {
        const isSelf = p.user_id === currentUserId;
        return (
          <div key={p.user_id} className="kb-card p-4" data-testid={`team-card-${p.user_id}`}>
            <div className="flex items-center gap-3">
              {p.picture ? (
                <img src={p.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-sm text-kb-primary">
                  {p.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-kb-ink truncate">{p.name} {isSelf && <span className="kb-mono text-[9px] text-kb-ink-light">(you)</span>}</div>
                <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted truncate">
                  {p.role_in_workspace}{p.affiliation ? ` · ${p.affiliation}` : ""}
                </div>
              </div>
            </div>
            {!isSelf && (
              <div className="mt-3 flex gap-2">
                <button onClick={() => onMessage(p)} className="kb-btn-outline text-xs flex-1" data-testid={`team-msg-${p.user_id}`}>
                  <MessageSquare className="w-3 h-3" /> Message
                </button>
                <button onClick={() => onVideo(p)} className="kb-btn-outline text-xs flex-1" data-testid={`team-call-${p.user_id}`}>
                  <Video className="w-3 h-3" /> Call
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Kanban with HTML5 drag-and-drop ----------
export function KanbanBoard({ workspaceId, participants }) {
  const [tasks, setTasks] = useState([]);
  const [adding, setAdding] = useState(null);
  const [draft, setDraft] = useState({ title: "", assignee_user_id: "" });
  const [dragId, setDragId] = useState(null);
  const [dropCol, setDropCol] = useState(null);

  const load = async () => {
    const r = await api.get(`/workspace/${workspaceId}/tasks`);
    setTasks(r.data || []);
  };
  useEffect(() => {
    load();
  }, [workspaceId]);

  const add = async (col) => {
    if (!draft.title.trim()) return;
    try {
      await api.post(`/workspace/${workspaceId}/tasks`, {
        guidance_id: workspaceId,
        title: draft.title.trim(),
        status: col,
        assignee_user_id: draft.assignee_user_id || null,
      });
      setDraft({ title: "", assignee_user_id: "" });
      setAdding(null);
      load();
    } catch (e) {
      toast.error("Failed to add task");
    }
  };
  const moveTo = async (taskId, status) => {
    setTasks((cur) => cur.map((t) => (t.id === taskId ? { ...t, status } : t))); // optimistic
    try {
      await api.put(`/workspace/tasks/${taskId}`, { status });
    } catch (e) {
      toast.error("Move failed");
      load();
    }
  };
  const remove = async (task) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/workspace/tasks/${task.id}`);
    load();
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kanban-board">
      {KANBAN_COLS.map((col) => {
        const items = tasks.filter((t) => t.status === col.key);
        const isDropping = dropCol === col.key;
        return (
          <div
            key={col.key}
            className={`border rounded-sm p-3 transition-shadow ${col.cls} ${isDropping ? "ring-2 ring-kb-primary" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDropCol(col.key);
            }}
            onDragLeave={() => setDropCol((c) => (c === col.key ? null : c))}
            onDrop={(e) => {
              e.preventDefault();
              setDropCol(null);
              if (dragId) {
                const t = tasks.find((x) => x.id === dragId);
                if (t && t.status !== col.key) moveTo(dragId, col.key);
              }
              setDragId(null);
            }}
            data-testid={`kanban-col-${col.key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                {col.label} · {items.length}
              </div>
              <button onClick={() => setAdding(col.key)} className="text-kb-ink-muted hover:text-kb-primary" data-testid={`kanban-add-${col.key}`}>
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {adding === col.key && (
              <div className="mb-2 bg-white border border-slate-200 rounded-sm p-2 space-y-2">
                <input
                  autoFocus
                  className="kb-input text-xs w-full"
                  placeholder="Task title…"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && add(col.key)}
                />
                <select
                  className="kb-input text-xs w-full"
                  value={draft.assignee_user_id}
                  onChange={(e) => setDraft({ ...draft, assignee_user_id: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {participants.map((p) => (
                    <option key={p.user_id} value={p.user_id}>{p.name}</option>
                  ))}
                </select>
                <div className="flex gap-1">
                  <button onClick={() => add(col.key)} className="kb-btn-primary text-xs flex-1">Add</button>
                  <button onClick={() => { setAdding(null); setDraft({ title: "", assignee_user_id: "" }); }} className="kb-btn-outline text-xs">Cancel</button>
                </div>
              </div>
            )}
            <div className="space-y-2 min-h-[60px]">
              {items.map((t) => {
                const assignee = participants.find((p) => p.user_id === t.assignee_user_id);
                return (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => {
                      setDragId(t.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => setDragId(null)}
                    className={`bg-white border border-slate-200 rounded-sm p-2.5 group cursor-grab active:cursor-grabbing ${dragId === t.id ? "opacity-40" : ""}`}
                    data-testid={`task-${t.id}`}
                  >
                    <div className="text-xs text-kb-ink leading-snug">{t.title}</div>
                    {assignee && (
                      <div className="mt-1.5 kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted">@ {assignee.name}</div>
                    )}
                    <div className="mt-2 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => remove(t)} className="text-rose-500 hover:text-rose-700">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Paper editor ----------
export function PaperEditor({ workspaceId }) {
  const [doc, setDoc] = useState({ title: "Untitled paper", body_md: "" });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const hostRef = useRef(null);

  useEffect(() => {
    api.get(`/workspace/${workspaceId}/paper-draft`).then((r) => {
      const d = r.data;
      // First-time open: fall back to the IMRaD scaffold instead of a blank
      // canvas so authors get an immediate structure to fill in.
      if (!d || (!d.body_md || !d.body_md.trim())) {
        setDoc({ title: d?.title || "Untitled paper", body_md: PAPER_TEMPLATE });
      } else {
        setDoc(d);
      }
    });
  }, [workspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspace/${workspaceId}/paper-draft`, {
        guidance_id: workspaceId,
        title: doc.title || "Untitled paper",
        body_md: doc.body_md || "",
      });
      setSavedAt(new Date());
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Native Fullscreen API on our own wrapper. Avoids MDEditor's built-in
  // fullscreen — that one uses `position:fixed` on its internal root which
  // fights our parent layout and produces the flicker on hover.
  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === hostRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = async () => {
    const el = hostRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) await document.exitFullscreen();
      else if (el.requestFullscreen) await el.requestFullscreen();
    } catch { setFullscreen((v) => !v); }
  };

  // Debounced auto-save — persists ~1.8s after typing stops.
  useEffect(() => {
    if (!doc.body_md && !doc.title) return;
    const t = setTimeout(() => { save(); }, 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.body_md, doc.title]);

  return (
    <div
      ref={hostRef}
      data-testid="paper-editor"
      data-color-mode="light"
      className={fullscreen ? "kb-wb-fs bg-white p-4 overflow-auto" : undefined}
    >
      <div className="flex items-center gap-3 mb-3">
        <input
          className="kb-input text-base font-serif flex-1"
          value={doc.title}
          onChange={(e) => setDoc({ ...doc, title: e.target.value })}
          placeholder="Paper title"
        />
        <button
          type="button"
          onClick={toggleFullscreen}
          className="p-1.5 text-kb-ink-muted hover:text-kb-ink border border-slate-200 rounded-sm"
          title={fullscreen ? "Exit full-screen" : "Full-screen editor"}
          data-testid="paper-fullscreen-toggle"
        >
          {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
        <button onClick={save} disabled={saving} className="kb-btn-primary text-sm">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {savedAt && (
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">
          Last saved: {savedAt.toLocaleTimeString()}
        </div>
      )}
      <MDEditor
        value={doc.body_md || ""}
        onChange={(v) => setDoc({ ...doc, body_md: v || "" })}
        height={fullscreen ? Math.max(400, window.innerHeight - 160) : 520}
        preview="live"
        hideToolbar={false}
        // Strip MDEditor's own fullscreen button — its `position:fixed`
        // overlay conflicts with our parent card and causes the flicker.
        extraCommands={[]}
      />
    </div>
  );
}

// ---------- Drive Tab ----------
export function DriveTab({ status, onConnect, workspaceTitle, workspaceId }) {
  const [folder, setFolder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [wsInfo, setWsInfo] = useState(null); // { folder_id, needs_setup, is_owner }
  const [setupBusy, setSetupBusy] = useState(false);
  const fileRef = useRef(null);

  // Fetch the shared workspace folder info regardless of whether the current
  // user has authorized Drive locally — the folder lives in the drive-owner's
  // account and is shared with everyone else via edit-mode permissions.
  useEffect(() => {
    if (!workspaceId) return;
    api.get(`/workspace/${workspaceId}/drive/info`)
      .then((r) => setWsInfo(r.data))
      .catch(() => setWsInfo(null));
  }, [workspaceId]);

  // Auto-trigger the shared folder setup once, silently. If the drive-owner
  // (assigner for tasks / client otherwise) has authorized Drive, this fills
  // in `folder_id`; otherwise the button below stays visible so someone can
  // prompt them.
  useEffect(() => {
    if (!wsInfo || !wsInfo.needs_setup) return;
    api.post(`/workspace/${workspaceId}/drive/setup`)
      .then((r) => setWsInfo((prev) => ({ ...(prev || {}), folder_id: r.data.folder_id, needs_setup: false })))
      .catch(() => { /* owner probably hasn't authorized Drive — keep the CTA */ });
  }, [wsInfo?.needs_setup, workspaceId]);

  const sharedRoot = wsInfo?.folder_id || null;

  const load = async (folderId = null) => {
    setLoading(true);
    try {
      const r = await api.get("/google/drive/list", {
        params: { folder_id: folderId || sharedRoot || undefined },
      });
      setItems(r.data.files || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drive list failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (status?.has_drive && sharedRoot) load(folder?.id || sharedRoot);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.has_drive, sharedRoot, folder]);

  // If the shared folder exists we can render an "Open in Drive" panel even
  // for users who haven't personally authorized Drive — they were shared the
  // folder in edit mode. Local Drive auth is only needed to browse/upload
  // files inline within KhojByte.
  const sharedFolderUrl = sharedRoot ? `https://drive.google.com/drive/folders/${sharedRoot}` : null;

  if (!status?.has_drive) {
    return (
      <div className="kb-card p-8 text-center" data-testid="drive-connect-cta">
        <FolderOpen className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-kb-ink mt-3">
          {sharedFolderUrl ? "Shared folder is ready" : "Google Drive folder not set up yet"}
        </h3>
        <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
          {sharedFolderUrl
            ? "The assigner has shared this workspace folder with your email in edit mode. Open it directly in Google Drive, or authorize Drive here to browse and upload files inline."
            : "The shared folder is created in the assigner/owner's Drive and auto-shared with everyone. It will appear here as soon as they authorize Drive."}
        </p>
        <div className="mt-5 flex flex-wrap gap-2 justify-center">
          {sharedFolderUrl && (
            <a href={sharedFolderUrl} target="_blank" rel="noreferrer" className="kb-btn-primary inline-flex" data-testid="drive-open-external">
              <FolderOpen className="w-4 h-4" /> Open in Google Drive
            </a>
          )}
          <button onClick={onConnect} className="kb-btn-outline inline-flex" data-testid="drive-connect-btn">
            <Link2 className="w-4 h-4" /> Authorize Drive
          </button>
        </div>
      </div>
    );
  }

  if (wsInfo && wsInfo.needs_setup) {
    return (
      <div className="kb-card p-8 text-center" data-testid="drive-setup-cta">
        <FolderPlus className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-kb-ink mt-3">Set up the shared workspace drive</h3>
        <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
          The shared workspace folder is created inside the assigner/owner&apos;s Drive at
          <em> KhojByte Website / [Research|Guidance|Task] Workspace / Year / Month / {workspaceTitle || workspaceId}</em>
          {" "}and auto-shared with every collaborator, mentor and later-added teammate in edit mode.
          {!wsInfo.is_drive_owner && <> The assigner/owner needs to have connected Google Drive at least once for this to succeed.</>}
        </p>
        <button
          onClick={async () => {
            setSetupBusy(true);
            try {
              const r = await api.post(`/workspace/${workspaceId}/drive/setup`);
              toast.success("Shared workspace drive ready");
              setWsInfo({ ...wsInfo, folder_id: r.data.folder_id, needs_setup: false });
            } catch (e) {
              const status = e?.response?.status;
              const detail = e?.response?.data?.detail || "Couldn't set up shared drive";
              // 428 → re-consent is needed. Only self-redirect when the
              // *current* user is the drive-owner; otherwise just surface
              // the message so they can ping the assigner.
              if (status === 428 && wsInfo?.is_drive_owner) {
                toast.message("Google Drive permission needed — redirecting to re-authorize…");
                requestExtraScopes(SCOPE_DRIVE);
                return;
              }
              toast.error(detail);
            } finally {
              setSetupBusy(false);
            }
          }}
          disabled={setupBusy}
          className="kb-btn-primary mt-5 inline-flex" data-testid="drive-setup-btn-tab"
        >
          {setupBusy ? "Setting up…" : "Set up shared workspace drive"}
        </button>
      </div>
    );
  }

  const createFolder = async () => {
    const name = newFolderName.trim() || `Sub-folder`;
    try {
      const r = await api.post("/google/drive/folder", { name, parent_id: folder?.id || sharedRoot || null });
      toast.success(`Folder "${r.data.name}" created`);
      setNewFolderName("");
      load(folder?.id || sharedRoot);
    } catch (e) {
      toast.error("Folder create failed");
    }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("folder_id", folder?.id || sharedRoot || "");
    try {
      await api.post("/google/drive/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to shared workspace drive");
      load(folder?.id || sharedRoot);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      e.target.value = "";
    }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete "${f.name}" from the shared workspace drive?`)) return;
    await api.delete(`/google/drive/${f.id}`);
    load(folder?.id || sharedRoot);
  };

  return (
    <div data-testid="drive-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {folder ? (
          <button onClick={() => setFolder(null)} className="kb-btn-outline text-xs">← Back</button>
        ) : null}
        <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          {folder ? `📁 ${folder.name}` : `📁 KhojByte / ${workspaceTitle || workspaceId} (shared)`}
        </div>
        <div className="flex-1" />
        <input className="kb-input text-xs" style={{ width: 200 }} placeholder="New folder name…" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} />
        <button onClick={createFolder} className="kb-btn-outline text-xs">
          <FolderPlus className="w-3 h-3" /> Create folder
        </button>
        <button onClick={() => fileRef.current?.click()} className="kb-btn-primary text-xs">
          <Upload className="w-3 h-3" /> Upload
        </button>
        <input ref={fileRef} type="file" onChange={upload} className="hidden" />
      </div>
      {loading ? (
        <div className="text-sm text-kb-ink-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
          Empty. Create a folder or upload a file to get started.
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm bg-white divide-y divide-slate-100">
          {items.map((f) => {
            const isFolder = f.mimeType === "application/vnd.google-apps.folder";
            return (
              <div key={f.id} className="flex items-center gap-3 px-3 py-2" data-testid={`drive-row-${f.id}`}>
                {isFolder ? (
                  <button onClick={() => setFolder({ id: f.id, name: f.name })} className="flex items-center gap-2 flex-1 text-left text-sm hover:text-kb-primary">
                    <FolderOpen className="w-4 h-4 text-kb-primary" /> {f.name}
                  </button>
                ) : (
                  <a href={f.webViewLink} target="_blank" rel="noreferrer" className="flex items-center gap-2 flex-1 text-sm hover:text-kb-primary">
                    <FileTextIcon className="w-4 h-4 text-kb-ink-muted" /> {f.name}
                  </a>
                )}
                <span className="kb-mono text-[10px] text-kb-ink-light">
                  {f.modifiedTime ? new Date(f.modifiedTime).toLocaleDateString() : ""}
                </span>
                <a href={f.webViewLink} target="_blank" rel="noreferrer" className="text-kb-ink-muted hover:text-kb-primary">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <button onClick={() => del(f)} className="text-rose-500 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Compact Scheduled-meet bar + Instant Meet button ----------
export function MeetingsBar({ workspaceId, status, onConnectMeet }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", scheduled_for: "", description: "", duration_minutes: 30, recurrence: "none" });
  const [starting, setStarting] = useState(false);
  const [activeMeet, setActiveMeet] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [exhausted, setExhausted] = useState(false);

  const load = async () => {
    const r = await api.get(`/workspace/${workspaceId}/scheduled-meets`);
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, [workspaceId]);

  // Ticker for the active instant meeting
  useEffect(() => {
    if (!activeMeet) return;
    const tick = () => {
      const end = new Date(activeMeet.free_tier_ends_at).getTime();
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setRemaining(s);
      if (s <= 0) setExhausted(true);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [activeMeet]);

  const upcoming = items.filter((m) => !m.scheduled_for || new Date(m.scheduled_for) >= new Date(Date.now() - 60 * 60 * 1000));
  const nextMeet = upcoming[0];

  const startInstant = async () => {
    if (!status?.has_meet) {
      onConnectMeet();
      return;
    }
    setStarting(true);
    try {
      const r = await api.post("/google/meet/create", { purpose: "workspace", context_id: workspaceId });
      setActiveMeet(r.data);
      setExhausted(false);
      window.open(r.data.meeting_uri, "_blank");
      // Broadcast so every collaborator's ActiveCallBanner lights up with a
      // Join Meeting button — same channel Daily.co uses.
      try {
        await api.post(`/workspace/${workspaceId}/call/start`, {
          provider: "google_meet",
          room_name: r.data.meeting_code || r.data.id,
          join_url: r.data.meeting_uri,
          audio_only: false,
        });
      } catch { /* non-fatal */ }
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Google Meet quota reached — try again in a minute.");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't start a meeting");
      }
    } finally {
      setStarting(false);
    }
  };

  const saveSchedule = async () => {
    if (!draft.title.trim() || !draft.scheduled_for) {
      toast.error("Title and date required");
      return;
    }
    let saved = null;
    try {
      const r = await api.post(`/workspace/${workspaceId}/scheduled-meets`, draft);
      saved = r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create scheduled meeting");
      return;
    }
    // Save succeeded — attaching the Meet link is best-effort.
    let meetLinkOk = false;
    if (status?.has_meet) {
      try {
        await api.post(`/workspace/scheduled-meets/${saved.id}/attach-link`);
        meetLinkOk = true;
      } catch (e) {
        // Don't roll back — meeting is saved without a link, user can retry
        // attach later. Surface the precise reason instead of the generic
        // "Failed to schedule" toast that used to appear.
        const reason = e?.response?.data?.detail || "Google Meet quota / token issue";
        toast.warning(`Meeting saved, but Meet link not attached: ${reason}. You can retry later.`);
      }
    }
    setDraft({ title: "", scheduled_for: "", description: "", duration_minutes: 30, recurrence: "none" });
    setAdding(false);
    load();
    if (meetLinkOk) toast.success("Meeting scheduled with Google Meet link");
    else if (!status?.has_meet) toast.success("Meeting scheduled (connect Google Meet to auto-add the link)");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this scheduled meeting?")) return;
    await api.delete(`/workspace/scheduled-meets/${id}`);
    load();
  };

  const mm = String(Math.floor((remaining || 0) / 60)).padStart(2, "0");
  const ss = String((remaining || 0) % 60).padStart(2, "0");

  return (
    <div className="border border-slate-200 rounded-sm bg-white mb-4" data-testid="meetings-bar">
      {/* Top compact strip: next meeting + instant meet button on right */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
        <Calendar className="w-3.5 h-3.5 text-kb-primary" />
        {nextMeet ? (
          <>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">SCHEDULED · GOOGLE MEET</div>
            <div className="text-sm text-kb-ink truncate min-w-0">{nextMeet.title}</div>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">
              {(() => {
                if (!nextMeet.scheduled_for) return "";
                const at = new Date(nextMeet.scheduled_for);
                const minsAway = Math.round((at - new Date()) / 60000);
                const when = at.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
                if (minsAway > 60) return when;
                if (minsAway > 0) return `${when} · starts in ${minsAway}m`;
                if (minsAway > -90) return `${when} · LIVE NOW`;
                return when;
              })()}
            </div>
            {nextMeet.meeting_uri && (
              <a href={nextMeet.meeting_uri} target="_blank" rel="noreferrer" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline">
                Join →
              </a>
            )}
          </>
        ) : (
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">NO UPCOMING MEETINGS</div>
        )}
        <button onClick={() => setAdding((v) => !v)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary ml-1" data-testid="meeting-schedule-toggle">
          {adding ? "× Cancel" : "+ Schedule for later"}
        </button>
        <div className="flex-1" />
        {activeMeet && !exhausted && (
          <div className="kb-mono text-xs tabular-nums px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-700 flex items-center gap-1.5">
            <ClockIcon className="w-3 h-3" /> Google Meet · {mm}:{ss}
            <a href={activeMeet.meeting_uri} target="_blank" rel="noreferrer" className="underline ml-1">Re-join</a>
          </div>
        )}
        {activeMeet && exhausted && (
          <button onClick={startInstant} className="kb-btn-outline text-xs" data-testid="meet-recreate-btn">
            <RotateCcw className="w-3 h-3" /> Re-create (60m limit)
          </button>
        )}
        <button
          onClick={startInstant}
          disabled={starting}
          className="kb-btn-primary text-xs whitespace-nowrap"
          data-testid="meet-instant-btn"
          title={status?.has_meet ? "Start an instant Google Meet right now" : "Connect Google Meet to enable"}
        >
          <Video className="w-3 h-3" /> {starting ? "Starting…" : "Start now · Google Meet"}
        </button>
      </div>

      {adding && (
        <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 grid sm:grid-cols-2 gap-2" data-testid="schedule-form">
          <div className="sm:col-span-2 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
            Schedule a future <span className="text-kb-primary">Google Meet</span> — a calendar invite + Meet link is generated automatically once you connect Google Meet.
          </div>
          <input className="kb-input text-sm sm:col-span-2" placeholder="Title (e.g. Sprint review)" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <input type="datetime-local" className="kb-input text-sm" value={draft.scheduled_for} onChange={(e) => setDraft({ ...draft, scheduled_for: e.target.value })} />
          <input className="kb-input text-sm" placeholder="Notes (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          <input type="number" min="10" step="5" className="kb-input text-sm" placeholder="Duration (min)" value={draft.duration_minutes} onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) || 30 })} data-testid="schedule-duration" />
          <select className="kb-input text-sm" value={draft.recurrence} onChange={(e) => setDraft({ ...draft, recurrence: e.target.value })} data-testid="schedule-recurrence">
            <option value="none">One-off</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
          </select>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="kb-btn-outline text-xs">Cancel</button>
            <button onClick={saveSchedule} className="kb-btn-primary text-xs">Schedule meeting{status?.has_meet ? " (auto-creates Meet link)" : ""}</button>
          </div>
        </div>
      )}

      {upcoming.length > 1 && (
        <details className="border-t border-slate-200">
          <summary className="px-4 py-2 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted cursor-pointer hover:text-kb-primary">
            All upcoming meetings · {upcoming.length}
          </summary>
          <div className="px-4 pb-3 space-y-2">
            {upcoming.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-sm p-2.5 bg-white flex items-center gap-3" data-testid={`sched-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-kb-ink truncate">{m.title}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                    {m.scheduled_for ? new Date(m.scheduled_for).toLocaleString() : ""}
                  </div>
                </div>
                {m.meeting_uri ? (
                  <a href={m.meeting_uri} target="_blank" rel="noreferrer" className="kb-btn-primary text-xs">
                    <Video className="w-3 h-3" /> Join
                  </a>
                ) : (
                  <span className="kb-mono text-[10px] text-kb-ink-light">No Meet link</span>
                )}
                <button onClick={() => remove(m.id)} className="text-rose-500 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------- Video Tab ----------
function VideoTab({ status, onConnect, workspaceId }) {
  const [meeting, setMeeting] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    if (!meeting) return;
    const tick = () => {
      const end = new Date(meeting.free_tier_ends_at).getTime();
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setRemaining(s);
      if (s <= 0) setExhausted(true);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [meeting]);

  const create = async () => {
    try {
      const r = await api.post("/google/meet/create", { purpose: "workspace", context_id: workspaceId });
      setMeeting(r.data);
      setExhausted(false);
    } catch (e) {
      if (e?.response?.status === 429) {
        setExhausted(true);
        toast.error("Google Meet quota reached — try again in a minute.");
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't start a meeting");
      }
    }
  };

  if (!status?.has_meet) {
    return (
      <div className="kb-card p-8 text-center" data-testid="meet-connect-cta">
        <Video className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-kb-ink mt-3">Connect Google Meet</h3>
        <p className="text-sm text-kb-ink-muted mt-2 max-w-md mx-auto">
          Authorize Google Meet to spin up secure video calls with collaborators. Free tier supports 60-minute group calls.
        </p>
        <button onClick={onConnect} className="kb-btn-primary mt-5 inline-flex" data-testid="meet-connect-btn">
          <Link2 className="w-4 h-4" /> Authorize Meet
        </button>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="kb-card p-8 text-center">
        <Video className="w-8 h-8 text-kb-primary mx-auto" strokeWidth={1.5} />
        <h3 className="font-serif text-xl text-kb-ink mt-3">Instant meeting</h3>
        <p className="text-sm text-kb-ink-muted mt-2">Generate a Google Meet link for this workspace.</p>
        <button onClick={create} className="kb-btn-primary mt-5 inline-flex" data-testid="meet-create-btn">
          <Video className="w-4 h-4" /> Start meeting
        </button>
      </div>
    );
  }

  const mm = String(Math.floor((remaining || 0) / 60)).padStart(2, "0");
  const ss = String((remaining || 0) % 60).padStart(2, "0");
  return (
    <div className="kb-card p-6" data-testid="meet-active-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Video className="w-5 h-5 text-kb-primary" />
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-kb-ink">Meeting active</div>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{meeting.meeting_code}</div>
        </div>
        <div className={`kb-mono text-sm tabular-nums px-3 py-1.5 rounded-sm border ${exhausted ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-kb-ink"}`}>
          <ClockIcon className="w-3.5 h-3.5 inline mr-1" /> {mm}:{ss}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href={meeting.meeting_uri} target="_blank" rel="noreferrer" className="kb-btn-primary text-sm">
          <Video className="w-3.5 h-3.5" /> Join meeting
        </a>
        <button onClick={() => navigator.clipboard.writeText(meeting.meeting_uri).then(() => toast.success("Link copied"))} className="kb-btn-outline text-sm">
          <Link2 className="w-3.5 h-3.5" /> Copy link
        </button>
        {exhausted && (
          <button onClick={create} className="kb-btn-outline text-sm" data-testid="meet-recreate-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Re-create meeting (60 min limit reached)
          </button>
        )}
      </div>
      <div className="mt-3 text-xs text-kb-ink-muted">
        Google free tier limits group meetings to 60 minutes. After the timer ends you can recreate the link with one click.
      </div>
    </div>
  );
}

// ---------- Floating Quick Action FAB ----------
function FloatingActions({ onChat, onVideo, hidden }) {
  if (hidden) return null;
  return (
    <div className="fixed bottom-4 left-4 z-40 flex flex-col gap-2" data-testid="workspace-fab">
      <button
        onClick={onVideo}
        className="w-12 h-12 rounded-full bg-kb-primary text-white shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
        title="Start instant video call"
        data-testid="fab-video-btn"
      >
        <Video className="w-5 h-5" />
      </button>
      <button
        onClick={onChat}
        className="w-12 h-12 rounded-full bg-white border border-slate-200 text-kb-ink shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
        title="Open chat"
        data-testid="fab-chat-btn"
      >
        <MessageSquare className="w-5 h-5" />
      </button>
    </div>
  );
}

// ---------- Main Hub ----------
export default function WorkspaceHub({ workspaceId, guidanceId, currentUserId, workspaceTitle, contextType }) {
  const wid = workspaceId || guidanceId;
  const [tab, setTab] = useState("team");
  const [participants, setParticipants] = useState([]);
  const [status, setStatus] = useState({ has_drive: false, has_meet: false });
  const [dmTarget, setDmTarget] = useState(null);

  const refreshAll = async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/workspace/${wid}/participants`),
        api.get("/auth/google/status"),
      ]);
      setParticipants(p.data || []);
      setStatus(s.data || {});
    } catch (e) {
      // ignore
    }
  };
  useEffect(() => {
    refreshAll();
  }, [wid]);

  const openVideo = async () => {
    if (!status.has_meet) {
      await requestExtraScopes(SCOPE_MEET);
      return;
    }
    setTab("video");
  };
  const openChat = () => {
    setTab("team");
    // If we have at least one OTHER participant, open DM with them by default
    const other = participants.find((p) => p.user_id !== currentUserId);
    if (other) setDmTarget(other);
  };
  const connectDrive = () => requestExtraScopes(SCOPE_DRIVE);
  const connectMeet = () => requestExtraScopes(SCOPE_MEET);

  return (
    <div className="mt-6" data-testid="workspace-hub">
      <MeetingsBar workspaceId={wid} status={status} onConnectMeet={connectMeet} currentUserId={currentUserId} />

      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm kb-mono tracking-wider uppercase text-[11px] whitespace-nowrap border-b-2 transition-colors ${
                active ? "border-kb-primary text-kb-primary bg-kb-primary/5" : "border-transparent text-kb-ink-muted hover:text-kb-ink hover:bg-slate-50"
              }`}
              data-testid={`workspace-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-5" style={{ paddingRight: dmTarget ? "min(380px, calc(100vw - 32px))" : 0, transition: "padding 0.2s ease" }}>
        {tab === "team" && <TeamPanel participants={participants} currentUserId={currentUserId} onMessage={(p) => setDmTarget(p)} onVideo={openVideo} />}
        {tab === "tasks" && <KanbanBoard workspaceId={wid} participants={participants} />}
        {tab === "paper" && <PaperEditor workspaceId={wid} />}
        {tab === "drive" && <DriveTab status={status} onConnect={connectDrive} workspaceTitle={workspaceTitle} workspaceId={wid} />}
      </div>

      <FloatingActions onChat={openChat} onVideo={openVideo} hidden={!!dmTarget} />

      {dmTarget && (
        <DMDrawer
          partner={dmTarget}
          currentUserId={currentUserId}
          workspaceId={wid}
          onClose={() => setDmTarget(null)}
          hasDrive={status?.has_drive}
        />
      )}
    </div>
  );
}
